// Task 201 D3/D4 — ChipInput primitive.
//
// A chip/tag input for entering short string lists (keywords, aliases,
// Slack channels, GitHub repos). Enforces per-item length and list-length
// caps with both hard and soft thresholds:
//   - `maxItems` (hard): prevents adding beyond this count
//   - `softMaxItems` (soft): shows a warning badge at this count
//   - `maxItemLength` (hard): trims/rejects items that exceed this length
//
// Accessibility:
//   - `role="list"` on the chip list; each chip is `role="listitem"`
//   - Delete button: `aria-label="Remove <chip value>"`
//   - The input describes itself via `aria-describedby` pointing at the
//     counter + optional error
//   - Focus returns to the input after deleting a chip (keyboard-friendly)
//
// Usage:
//   <ChipInput
//     label="Keywords"
//     values={keywords}
//     onChange={setKeywords}
//     maxItems={20}
//     softMaxItems={18}
//     maxItemLength={32}
//     placeholder="Add keyword..."
//   />

import { useId, useRef, useState } from 'react';
import styles from './ChipInput.module.css';

interface ChipInputProps {
  /** Controlled list of current items. */
  values: string[];
  /** Called with the new list whenever items are added or removed. */
  onChange: (values: string[]) => void;
  /** Field label rendered above the chip list + input. */
  label?: string;
  /** Placeholder text for the text input. */
  placeholder?: string;
  /** Hard maximum number of items. Defaults to 20. */
  maxItems?: number;
  /**
   * Soft maximum — triggers a warning colour on the counter but does not block
   * input. Defaults to `maxItems - 2`.
   */
  softMaxItems?: number;
  /** Maximum character length per item. Defaults to 32. */
  maxItemLength?: number;
  /** Hint text rendered below the input. */
  hint?: string;
  /** Shown in red below the input; overrides hint. */
  error?: string;
  /** Passed to the root wrapper for external class composition. */
  className?: string;
}

/**
 * Chip/tag multi-value input.
 *
 * Press Enter or comma to confirm a chip. Backspace on an empty input removes
 * the last chip. Delete buttons on each chip remove that chip by value.
 */
export function ChipInput({
  values,
  onChange,
  label,
  placeholder = 'Add item…',
  maxItems = 20,
  softMaxItems,
  maxItemLength = 32,
  hint,
  error,
  className,
}: ChipInputProps) {
  const uid = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState('');

  const softLimit = softMaxItems ?? Math.max(maxItems - 2, 0);
  const atHardLimit = values.length >= maxItems;
  const atSoftLimit = values.length >= softLimit;
  const countId = `${uid}-count`;
  const hintId = hint ? `${uid}-hint` : undefined;
  const errorId = error ? `${uid}-error` : undefined;
  const draftErrorId = draftError ? `${uid}-draft-error` : undefined;

  function commit(raw: string) {
    const item = raw.trim();
    if (!item) {
      setDraft('');
      setDraftError('');
      return;
    }
    if (item.length > maxItemLength) {
      setDraftError(`Each item must be ≤ ${maxItemLength} characters (this is ${item.length}).`);
      return;
    }
    if (atHardLimit) {
      setDraftError(`Maximum ${maxItems} items.`);
      return;
    }
    if (values.includes(item)) {
      // Silently ignore duplicates; don't surface an error.
      setDraft('');
      setDraftError('');
      return;
    }
    onChange([...values, item]);
    setDraft('');
    setDraftError('');
  }

  function removeAt(index: number) {
    const next = values.filter((_, i) => i !== index);
    onChange(next);
    // Return focus to the input so keyboard users can continue.
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  const counterCls = [
    styles.counter,
    atHardLimit ? styles.counterHard : atSoftLimit ? styles.counterSoft : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputCls = [styles.input, atHardLimit ? styles.inputDisabled : '']
    .filter(Boolean)
    .join(' ');

  const describedBy = [draftErrorId ?? errorId ?? hintId, countId].filter(Boolean).join(' ');

  return (
    <div className={[styles.wrapper, className ?? ''].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={`${uid}-input`} className={styles.label}>
          {label}
        </label>
      )}

      {/* Counter */}
      <span id={countId} className={counterCls} aria-live="polite">
        {values.length} / {maxItems}
      </span>

      {/* Chips */}
      {values.length > 0 && (
        <ul className={styles.chipList} role="list" aria-label={label ? `${label} items` : 'Items'}>
          {values.map((v, i) => (
            <li key={v} className={styles.chip} role="listitem">
              <span className={styles.chipText}>{v}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${v}`}
                onClick={() => removeAt(i)}
              >
                {/* thin × */}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <line
                    x1="1"
                    y1="1"
                    x2="9"
                    y2="9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="9"
                    y1="1"
                    x2="1"
                    y2="9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        id={`${uid}-input`}
        type="text"
        className={inputCls}
        placeholder={atHardLimit ? `Maximum ${maxItems} items reached` : placeholder}
        disabled={atHardLimit}
        value={draft}
        aria-describedby={describedBy || undefined}
        aria-invalid={!!(draftError || error) || undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          if (draftError) setDraftError('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit(draft.replace(/,$/, ''));
          } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
            removeAt(values.length - 1);
          }
        }}
        onBlur={() => {
          if (draft.trim()) commit(draft);
        }}
      />

      {/* Draft error (per-item validation) */}
      {draftError && (
        <span id={draftErrorId} className={styles.error} role="alert">
          {draftError}
        </span>
      )}

      {/* External error */}
      {!draftError && error && (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      )}

      {/* Hint */}
      {!draftError && !error && hint && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
}
